import './Form.scss'
import { useForm } from 'react-hook-form'
import { Field, Input, Switch, Textarea } from '@fluentui/react-components'
import { useDotnetClient } from '@/contexts/DotnetClientContext'
import { Button } from '../Button'
import { toast } from 'react-toastify'
import { useEffect } from 'react'
import { RestSchemas } from '@/contexts/QueryProvider'

type MessagesChatsNewChat = RestSchemas['Messages.Chat.NewChat']
type MessagesChatsExistingChat = RestSchemas['Messages.Chat.ExistingChat']

type AddChatFormProps = {
  onSubmit: () => void
  edit?: boolean
  selectedChat?: MessagesChatsExistingChat
  applicationId: number
  mode: 'chat' | 'settings' | 'template'
}

type TChatForm = {
  applicationId: number
  id?: number
  projectId?: number
  key: string
  name: string
  description: string
  locked: boolean
  langchainConfig: string
  promptTemplate: string
}

export const AddChatForm = (props: AddChatFormProps) => {
  const { dmpClient, isDMPClientLoaded } = useDotnetClient()

  const {
    register,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<TChatForm>({
    defaultValues:
      props.edit === true
        ? ({
            id: props.selectedChat?.id || undefined,
            applicationId: props.applicationId,
            key: props.selectedChat?.key || '',
            name: props.selectedChat?.name || '',
            description: props.selectedChat?.description || '',
            locked: props.selectedChat?.locked,
            langchainConfig: props.selectedChat?.langchainConfig || '{}',
            promptTemplate: props.selectedChat?.promptTemplate || '',
          } as TChatForm)
        : ({
            applicationId: props.applicationId,
            projectId: 0,
            key: '',
            name: '',
            description: '',
            locked: false,
            langchainConfig: '{}',
            promptTemplate: '',
          } as TChatForm),
  })

  useEffect(() => {
    if (isDMPClientLoaded) {
      reset()
    }
  }, [isDMPClientLoaded, reset])

  const onSubmit = (data: TChatForm) => {
    if (props.edit && props.selectedChat) {
      const updatedChat: MessagesChatsExistingChat = {
        id: data.id!,
        ...data,
        applicationId: props.applicationId,
      }

      dmpClient?.chatApi
        .postChatApiPostChat(props.applicationId, updatedChat)
        .then(() => {
          toast.success('Chat successfully updated')
          props.onSubmit()
          reset()
        })
        .catch((err) => {
          console.error(err)
          toast.error('Error updating chat')
          props.onSubmit()
        })
    } else {
      const newChat: MessagesChatsNewChat = {
        ...data,
        applicationId: props.applicationId,
      }

      dmpClient?.chatApi
        .putChatApiAddChat(props.applicationId, newChat)
        .then(() => {
          toast.success('Chat successfully added')
          props.onSubmit()
          reset()
        })
        .catch((err) => {
          console.error(err)
          toast.error('Error adding chat')
          props.onSubmit()
        })
    }
  }

  return (
    <div className="form-wrapper">
      <div>
        {props.mode == 'chat' && (
          <>
            {/* Key Field */}
            <Field
              label="Key"
              required
              validationState={errors.name ? 'error' : 'none'}
              validationMessage={errors.name?.message}
            >
              <Input
                {...register('key', {
                  required: 'Key is required',
                  maxLength: 10,
                  setValueAs: (value) => value ?? undefined,
                })}
              />
            </Field>

            {/* Name Field */}
            <Field
              label="Name"
              required
              validationState={errors.name ? 'error' : 'none'}
              validationMessage={errors.name?.message}
            >
              <Input
                {...register('name', {
                  required: 'Name is required',
                  maxLength: 60,
                  setValueAs: (value) => value ?? undefined,
                })}
              />
            </Field>

            {/* Description Field */}
            <Field
              label="Description"
              validationState={errors.description ? 'error' : 'none'}
              validationMessage={errors.description?.message}
            >
              <Input
                {...register('description', {
                  maxLength: 256,
                  setValueAs: (value) => value ?? undefined,
                })}
              />
            </Field>

            <Field
              label="Locked?"
              required
              validationState={errors.description ? 'error' : 'none'}
              validationMessage={errors.description?.message}
            >
              <Switch {...register('locked')} />
            </Field>
          </>
        )}
        {props.mode == 'settings' && (
          <>
            <Field
              label="Settings (LangChain Config)"
              validationState={errors.langchainConfig ? 'error' : 'none'}
              validationMessage={errors.langchainConfig?.type === 'maxLength' ? 'Max length exceeded' : undefined}
            >
              <Textarea {...register('langchainConfig')} rows={10} />
            </Field>
          </>
        )}
        {props.mode == 'template' && (
          <>
            <Field
              label="Prompt Template"
              validationState={errors.promptTemplate ? 'error' : 'none'}
              validationMessage={errors.promptTemplate?.type === 'maxLength' ? 'Max length exceeded' : undefined}
            >
              <Textarea {...register('promptTemplate')} rows={10} />
            </Field>
          </>
        )}
      </div>

      {/* Submit Button */}
      <div className="form-action-container">
        <Button text="Submit" onClick={handleSubmit(onSubmit)} />
      </div>
    </div>
  )
}
